import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { get } from "../lib/http.js";
import { API } from "../config/api.js";
import {
  isPremiumEligibleEvent,
  getEventScheduleParts,
  canBookEvent,
  resolveEventPosterUrl,
} from "../api/events.js";
import EventCard from "../components/EventCard.jsx";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await get(API.events.getEvent);
        const all = Array.isArray(res?.events) ? res.events : [];
        if (alive) {
          setEvents(all);
        }
      } catch (err) {
        console.error("Search fetch error:", err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const results = useMemo(() => {
    if (!q.trim()) return [];
    const query = q.toLowerCase().trim();
    
    return events.filter(ev => {
      // Basic text fields to search
      const title = (ev?.name || ev?.title || "").toLowerCase();
      const desc = (ev?.description || ev?.about || "").toLowerCase();
      const category = (ev?.category || ev?.type || "").toLowerCase();
      
      // Venue and location
      const venueName = (ev?.venue?.name || "").toLowerCase();
      const venueCity = (ev?.venue?.city || "").toLowerCase();
      const location = (ev?.location || "").toLowerCase();
      
      // Artists / Casts (if available)
      const artists = (ev?.artists || []).map(a => (a?.name || "").toLowerCase()).join(" ");
      const cast = (ev?.cast || []).map(c => (c?.name || "").toLowerCase()).join(" ");
      
      return (
        title.includes(query) ||
        category.includes(query) ||
        venueName.includes(query) ||
        venueCity.includes(query) ||
        location.includes(query) ||
        artists.includes(query) ||
        cast.includes(query) ||
        (desc.length < 200 && desc.includes(query)) // Search small descriptions
      );
    });
  }, [events, q]);

  // Map to common card format
  const mappedResults = useMemo(() => {
    return results.map(ev => {
      const schedule = getEventScheduleParts(ev);
      return {
        id: ev?._id || ev?.id,
        title: ev?.name || ev?.title || "Event",
        subtitle: ev?.venue?.name
          ? `${ev.venue.name}${ev?.venue?.city ? `, ${ev.venue.city}` : ""}`
          : ev?.location || "",
        location: ev?.venue?.name
          ? `${ev.venue.name}${ev?.venue?.city ? `, ${ev.venue.city}` : ""}`
          : ev?.location || "",
        image: resolveEventPosterUrl(ev),
        category: ev?.category || ev?.type || "Event",
        schedule,
        dateFull: schedule.full || "",
        languages: ev?.language,
        status: ev?.status,
        canBook: canBookEvent(ev),
        price: ev?.ticketCategories?.filter(t => t?.isTicketAvailable !== false).map(t => Number(t.price)).filter(n => !Number.isNaN(n)).sort((a,b)=>a-b)[0] || 0
      };
    });
  }, [results]);

  const handleClear = () => {
    setSearchParams({});
  };

  return (
    <main className="min-h-[70vh] bg-slate-50 dark:bg-slate-950 px-4 py-8 md:py-12 transition-colors">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px) }
          to { opacity: 1; transform: none }
        }
      `}</style>
      <div className="mx-auto max-w-7xl">
        <header className="mb-8 md:mb-12">
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white md:text-4xl">
            Search Results
          </h1>
          {q && (
            <p className="mt-2 text-slate-600 dark:text-slate-400">
              Showing results for <span className="font-semibold text-slate-900 dark:text-amber-500">"{q}"</span>
              {" • "}{mappedResults.length} found
            </p>
          )}
        </header>

        {!q.trim() ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="h-16 w-16 mb-4 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center text-slate-400 dark:text-slate-500">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Search for events</h2>
            <p className="text-slate-500 dark:text-slate-400 max-w-md mx-auto">
              Type in the search bar above to find concerts, artists, stand-up comedy, and more.
            </p>
          </div>
        ) : loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-amber-400 border-t-transparent" />
          </div>
        ) : mappedResults.length > 0 ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 sm:gap-x-6 sm:gap-y-10 transition-all duration-500 ease-in-out">
            {mappedResults.map((ev, i) => (
              <div 
                key={ev.id} 
                className="flex justify-center transition-all duration-500 ease-in-out"
                style={{
                  animation: `fadeIn 0.4s ease-out ${i * 0.05}s both`
                }}
              >
                <Link to={`/events/${ev.id}`} className="block w-full h-full max-w-[280px] hover:-translate-y-1 hover:shadow-lg transition-all duration-300 rounded-xl">
                  <EventCard item={ev} />
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
            <div className="h-16 w-16 mb-4 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center text-rose-500">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No results found</h2>
            <p className="text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-6">
              We couldn't find any events matching "{q}". Try checking your spelling or using more general keywords.
            </p>
            <button
              onClick={handleClear}
              className="px-6 py-2 bg-slate-900 dark:bg-amber-500 text-white dark:text-slate-900 font-bold rounded-full shadow-md hover:bg-slate-800 dark:hover:bg-amber-400 transition-colors"
            >
              Clear Search
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
